import { createServerClient as createSupabaseServerClient, createBrowserClient as createSupabaseBrowserClient } from '@supabase/ssr';
import { Database } from './database.types';
import { createLogger } from './utils/logger';
import type { SupabaseClient as SupabaseClientType } from '@supabase/supabase-js';
import { getSupabaseConfig as getSupabaseEnvConfig } from './utils/env-config';
import type { NextRequest, NextResponse } from 'next/server';

const logger = createLogger('supabase:clients');

// Common configuration - use centralized env config
function getSupabaseConfig() {
  const envConfig = getSupabaseEnvConfig();
  
  if (!envConfig.url || !envConfig.anonKey) {
    console.warn('[Supabase] Missing environment variables - using defaults');
  }
  
  return {
    url: envConfig.url || 'https://lpjvndsvkvqgkdkvacnn.supabase.co',
    anonKey: envConfig.anonKey || '',
    serviceKey: envConfig.serviceKey
  };
}

// Extract project reference for cookie naming
function getProjectRef(): string {
  // Use the URL from environment or config depending on context
  const url = typeof window !== 'undefined' 
    ? process.env.NEXT_PUBLIC_SUPABASE_URL 
    : getSupabaseConfig().url;
  const match = url?.match(/https:\/\/([^.]+)\.supabase\.co/);
  return match?.[1] || 'lpjvndsvkvqgkdkvacnn';
}

// Cookie name getter
function getCookieName(): string {
  return `sb-${getProjectRef()}-auth-token`;
}

// Export specific type for our database
export type SupabaseClient = SupabaseClientType<Database>;

// Singleton instance for browser client to prevent multiple instances
let browserClient: SupabaseClient | null = null;

// Hard cap (ms) on how long we will wait to ACQUIRE the cross-tab auth Web Lock
// before proceeding without it. auth-js asks for this lock with acquireTimeout=-1
// ("wait forever") around initialize()/getSession(); on Android Chrome the lock is
// a navigator.locks exclusive lock, and Chrome freezes background tabs WITHOUT
// releasing their Web Locks. A frozen sibling tab therefore wedges auth init in the
// foreground tab permanently → the "Authentication Taking Too Long" dead-end
// (see memory: project_auth_init_stall). We cap acquisition and fall through.
const AUTH_LOCK_ACQUIRE_MAX_WAIT_MS = 5000;

/**
 * Cross-tab auth lock with a bounded acquire time.
 *
 * Drop-in replacement for auth-js's built-in `navigatorLock`, with one crucial
 * difference: it never waits forever. auth-js passes `acquireTimeout` of -1
 * (infinite) for its critical sections; the stock navigatorLock only arms an
 * abort timer when `acquireTimeout > 0`, so an infinite acquire can hang the
 * whole client if another (possibly frozen) tab holds the lock. Here we impose a
 * hard cap on ACQUISITION only — once acquired, `fn` runs to completion holding
 * the lock exactly as before. If acquisition exceeds the cap we run `fn` WITHOUT
 * the lock: a rare duplicated token refresh is harmless (auth-js has refresh-token
 * reuse grace), whereas an infinite hang is a hard outage.
 *
 * The `acquireTimeout === 0` case (auto-refresh tick's opportunistic "skip if
 * busy" acquire) preserves the stock ifAvailable semantics and error contract.
 */
export async function boundedBrowserAuthLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  const nav: any = (globalThis as any)?.navigator;

  // Web Locks API unavailable (SSR / very old browsers) → run without cross-tab
  // coordination, matching auth-js's own lockNoOp fallback.
  if (!nav?.locks?.request) {
    return await fn();
  }

  // Opportunistic acquire (auth-js auto-refresh tick). Preserve ifAvailable +
  // the duck-typed `isAcquireTimeout` error so auth-js can "another tab is
  // refreshing, skip" exactly as with the stock lock.
  if (acquireTimeout === 0) {
    return await Promise.resolve().then(() =>
      nav.locks.request(
        name,
        { mode: 'exclusive', ifAvailable: true },
        async (lock: any) => {
          if (!lock) {
            const err: any = new Error(
              `Acquiring an exclusive Navigator LockManager lock "${name}" immediately failed`,
            );
            err.isAcquireTimeout = true;
            throw err;
          }
          return await fn();
        },
      ),
    );
  }

  // "Wait forever" (-1) or a positive timeout → cap the wait. Positive timeouts
  // are honored up to the cap; -1 collapses to the cap.
  const cap =
    acquireTimeout > 0
      ? Math.min(acquireTimeout, AUTH_LOCK_ACQUIRE_MAX_WAIT_MS)
      : AUTH_LOCK_ACQUIRE_MAX_WAIT_MS;
  const abortController = new AbortController();
  const timer = setTimeout(() => abortController.abort(), cap);

  try {
    return await Promise.resolve().then(() =>
      nav.locks.request(
        name,
        { mode: 'exclusive', signal: abortController.signal },
        async () => {
          clearTimeout(timer);
          return await fn();
        },
      ),
    );
  } catch (err: any) {
    clearTimeout(timer);
    // AbortError = we hit the cap without acquiring. Proceed unlocked rather than
    // hang. Any other error is a genuine failure inside fn → propagate.
    if (err?.name === 'AbortError' || err?.isAcquireTimeout) {
      console.warn(
        `[supabase-lock] auth lock "${name}" not acquired within ${cap}ms; ` +
          'proceeding without the cross-tab lock (a background tab may be holding it)',
      );
      return await fn();
    }
    throw err;
  }
}

/**
 * Creates a Supabase client for server components
 *
 * Use this client in server components to access Supabase services
 * with the user's authentication context.
 *
 * IMPORTANT: This is an async function in Next.js 15 - must be awaited!
 */
export async function createServerClient() {
  try {
    // Dynamic import to avoid issues in client components
    const { cookies } = require('next/headers');
    // Get cookie store directly from Next.js - cookies() returns Promise in Next.js 15
    const cookieJar = await cookies();
    const config = getSupabaseConfig();
    
    // Create server client with cookie manager for consistent cookie handling
    return createSupabaseServerClient<Database>(
      config.url,
      config.anonKey,
      {
        cookies: {
          get(name) {
            const cookie = cookieJar.get(name);
            return cookie?.value || null;
          },
          set(name, value, options) {
            // Server components can't set cookies - this is expected behavior
            // Supabase may try to refresh tokens, but this will be handled by middleware
          },
          remove(name, options) {
            // Server components can't remove cookies - this is expected behavior
          }
        }
      }
    ) as SupabaseClient;
  } catch (error) {
    logger.error('Failed to create server client', error);
    // Return a fallback client that will work but with no auth
    const config = getSupabaseConfig();
    return createSupabaseServerClient<Database>(
      config.url,
      config.anonKey,
      {
        cookies: {
          get: () => null,
          set: () => {},
          remove: () => {}
        }
      }
    ) as SupabaseClient;
  }
}

/**
 * Creates a Supabase client for client components
 * 
 * Use this client in client components to access Supabase
 * services with the user's authentication context.
 * Uses singleton pattern to prevent multiple client instances.
 */
export function createBrowserClient() {
  if (!browserClient) {
    // In the browser, we need to use the environment variables directly
    // as they are replaced at build time by Next.js
    const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    
    if (!url || !anonKey) {
      throw new Error(
        '@supabase/ssr: Your project\'s URL and API key are required to create a Supabase client!\n\n' +
        'Check your Supabase project\'s API settings to find these values\n\n' +
        'https://supabase.com/dashboard/project/_/settings/api'
      );
    }
    
    // For browser clients, @supabase/ssr handles cookies automatically
    // No need to configure cookie options - it uses document.cookie internally.
    //
    // We override the auth `lock` with a bounded-acquire variant so a frozen
    // background tab holding the navigator.locks auth lock can never wedge
    // getSession()/initialize() forever (the mobile "Authentication Taking Too
    // Long" dead-end — see memory: project_auth_init_stall). All other auth
    // options (storageKey, flowType 'pkce', autoRefreshToken, persistSession,
    // detectSessionInUrl, cookie storage) are left to @supabase/ssr defaults.
    browserClient = createSupabaseBrowserClient<Database>(
      url,
      anonKey,
      {
        auth: {
          lock: boundedBrowserAuthLock,
        },
      },
    ) as SupabaseClient;
  }
  return browserClient;
}

/**
 * Creates a Supabase client for Route Handlers (API routes)
 *
 * Use this client in API route handlers to access Supabase
 * services with the user's authentication context.
 * This client can properly set and remove cookies in route handlers.
 *
 * IMPORTANT: This is an async function in Next.js 15 - must be awaited!
 */
export async function createRouteHandlerClient() {
  // Dynamic import to avoid issues in client components
  const { cookies } = require('next/headers');
  // cookies() returns Promise in Next.js 15
  const cookieJar = await cookies();
  const config = getSupabaseConfig();

  return createSupabaseServerClient<Database>(
    config.url,
    config.anonKey,
    {
      cookies: {
        get(name) {
          const cookie = cookieJar.get(name);
          return cookie?.value || null;
        },
        set(name, value, options) {
          try {
            cookieJar.set({ name, value, ...options });
          } catch (error) {
            logger.warn(`[RouteHandler] Cannot set cookie ${name}:`, error);
          }
        },
        remove(name, options) {
          try {
            cookieJar.set({ name, value: '', ...options });
          } catch (error) {
            logger.warn(`[RouteHandler] Cannot remove cookie ${name}:`, error);
          }
        }
      }
    }
  ) as SupabaseClient;
}

/**
 * Creates a Supabase client for Next.js Middleware
 * 
 * Use this client in middleware.ts to handle authentication
 * and session management. This client properly handles cookies
 * in the middleware context.
 */
export function createMiddlewareClient(
  request: NextRequest,
  response: NextResponse
) {
  const config = getSupabaseConfig();
  
  return createSupabaseServerClient<Database>(
    config.url,
    config.anonKey,
    {
      cookies: {
        get(name) {
          return request.cookies.get(name)?.value || null;
        },
        set(name, value, options) {
          // Set cookie on response
          response.cookies.set({
            name,
            value,
            ...options,
          });
        },
        remove(name, options) {
          // Remove cookie on response
          response.cookies.set({
            name,
            value: '',
            ...options,
            maxAge: 0,
          });
        }
      }
    }
  ) as SupabaseClient;
}

